import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Loader2, ShieldAlert, XCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { appName } from '../../config/branding';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Shared admin auth gate.
 *
 * Renders one of three states:
 *   1. Not signed in → "Admin sign-in required" card with Continue-with-Google button.
 *   2. Signed in but not admin → "Access denied" card showing their email.
 *   3. Signed in admin → renders `children`, passing `{ adminEmail }` via render-prop
 *      so the inner page can use it (e.g. in the AdminNav).
 *
 * Verifies admin status by hitting `/api/auth/whoami` and checking `roles.admin`.
 * This is the single source of truth for who is an admin; the backend endpoints
 * all independently re-check via `require_admin_user`.
 */
const AdminGate = ({ children }) => {
  const { user, loading: authLoading, login } = useAuth();
  const [whoami, setWhoami] = useState(null);
  const [checking, setChecking] = useState(true);
  const [networkError, setNetworkError] = useState('');

  const loadWhoami = useCallback(async () => {
    setChecking(true);
    setNetworkError('');
    try {
      const res = await axios.get(`${API}/auth/whoami`, { withCredentials: true });
      setWhoami(res.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        setWhoami(null);
      } else {
        setNetworkError(e?.response?.data?.detail || 'Failed to verify admin access.');
      }
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setChecking(false); setWhoami(null); return; }
    loadWhoami();
  }, [user, authLoading, loadWhoami]);

  // State 0: still resolving auth
  if (authLoading || checking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-gray-400" />
      </div>
    );
  }

  // State 1: not signed in
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b border-gray-200">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold text-[#37b34a]">{appName}</Link>
            <Link to="/" className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1.5 no-underline">
              <ArrowLeft size={14} /> Back to home
            </Link>
          </div>
        </header>
        <main className="max-w-md mx-auto px-4 sm:px-6 py-16">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
              <ShieldAlert className="text-amber-500" size={22} />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Admin sign-in required</h1>
            <p className="text-sm text-gray-500 mb-6">
              Sign in with an authorised Google admin account to continue.
            </p>
            <button
              data-testid="admin-gate-login-btn"
              onClick={login}
              className="w-full bg-[#37b34a] hover:bg-[#2d9a3e] text-white font-semibold py-2.5 rounded-lg transition-colors"
            >
              Continue with Google
            </button>
          </div>
        </main>
      </div>
    );
  }

  // State 2: signed in but not admin
  if (!whoami?.roles?.admin) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b border-gray-200">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold text-[#37b34a]">{appName}</Link>
          </div>
        </header>
        <main className="max-w-md mx-auto px-4 sm:px-6 py-16">
          <div
            data-testid="admin-gate-forbidden"
            className="bg-white rounded-2xl border border-red-100 shadow-sm p-8 text-center"
          >
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <XCircle className="text-red-500" size={22} />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Access denied</h1>
            <p className="text-sm text-gray-500 mb-1">
              Signed in as <span className="font-semibold text-gray-900">{user.email}</span>
            </p>
            <p className="text-sm text-gray-500">
              This account is not on the admin allow-list.
            </p>
            {networkError && (
              <p className="text-xs text-red-500 mt-3">{networkError}</p>
            )}
          </div>
        </main>
      </div>
    );
  }

  // State 3: admin — render page content
  return typeof children === 'function' ? children({ adminEmail: user.email }) : children;
};

export default AdminGate;
